import { TFunction } from 'i18next';
import { areDifferent } from 'utils/array';
import { getDateTimeDiff, toLocaleDateShort } from 'utils/datetime';
import { newGuidNil } from 'utils/guid';
import { htmlToText } from 'utils/html';
import KPI, { KPITypes } from './kpi';
import KPIDataContext from './kpiDataContext';
import KPIDataDefinition from './kpiDataDefinition';

export default class KPIData {
  //must match [StringLength(4000)] on KPIDataDTO.ResultComment in the API: the comment is stored as HTML
  static commentMaxLength = 4000;

  //must match [StringLength(1024)] on KPIDataDTO.ResultText in the API
  static textMaxLength = 1024;

  kpiId: number;

  kpiDataId: number;

  kpiDefinitionRevisionId?: number;

  // Server-owned display snapshot of the definition that governed this value. Direct edits retain
  // the current KPI reference below, while history formats values with this immutable revision.
  definition?: KPIDataDefinition;

  created: Date;

  createdBy: string;

  createdById: string;

  modified?: Date;

  modifiedBy?: string;

  modifiedById?: string;

  resultDate?: Date;

  resultNumber?: number;

  resultText?: string;

  resultComment?: string;

  // True while the value is what the form put there (the definition's default at load, a computed
  // expression result) and the user did not change it. Sent to the server, which stores it and reads
  // it as "not a recorded answer" (KPIDataAnswerRules). Cleared by TaskTypeForm.onUpdateValue.
  isSeeded: boolean;

  resultEvidence?: string;

  // The AI verdict on this same question, written by the audit run. Read-only for the client: it is
  // never sent back, so the human answer above and the AI answer here can never overwrite each other.
  aiResultNumber?: number;

  aiResultText?: string;

  aiResultDate?: Date;

  aiResultComment?: string;

  aiAssessedOn?: Date;

  aiRunRef?: string;

  contexts: KPIDataContext[];

  kpi?: KPI;

  constructor() {
    this.kpiId = 0;
    this.kpiDataId = 0;
    this.created = new Date();
    this.createdBy = '';
    this.createdById = newGuidNil();
    this.isSeeded = false;
    this.contexts = [];
  }

  hasValue(): boolean {
    return this.getValue() !== undefined;
  }

  // Whether a HUMAN actually put something in this row. Deliberately NOT hasValue(): that one asks
  // the KPI type to format the answer, and a Number formats as "0" and a Choice as "undefined -
  // undefined" even when nobody answered, so it can never say "empty". The comment counts - it is the
  // auditor's own work - which is also how the server reads it (GetAnsweredFieldKPIIdsAsync).
  hasAnswer(): boolean {
    return (
      this.resultNumber !== undefined || this.resultDate !== undefined || !!this.resultText || !!this.resultComment
    );
  }

  // Whether a person recorded an answer here: the rule the server applies before it moves a task to
  // a newer form version and before it lets a task replace its form (KPIDataAnswerRules). Narrower
  // than hasAnswer(): a value the form seeded (the definition's default at load, a computed expression
  // result) is not one until the user changes it, which clears isSeeded. A comment always counts.
  // Attachments are the caller's to count.
  isRecordedAnswer(): boolean {
    if (this.resultComment) return true;

    return !this.isSeeded && (this.resultNumber !== undefined || this.resultDate !== undefined || !!this.resultText);
  }

  getValue(t?: TFunction<string[]>): string | undefined {
    const type = this.definition?.type ?? this.kpi?.type;
    const decimalCount = this.definition?.decimalCount ?? this.kpi?.decimalCount ?? 0;
    if (type !== undefined) {
      switch (type) {
        case KPITypes.Choice:
          const choiceKey = this.resultNumber === undefined ? undefined : Math.floor(this.resultNumber);
          const choice = this.definition?.choices.find((item) => item.choiceKey === choiceKey);

          return `${this.resultNumber} - ${choice?.value ?? this.resultText}`;
        case KPITypes.Number:
          return (this.resultNumber ?? 0).toFixed(decimalCount);
        case KPITypes.SuccesError:
          if (t) {
            return (this.resultNumber ?? 0) > 0
              ? t('translation:General.Words.Yes')
              : t('translation:General.Words.No');
          } else {
            return this.resultNumber?.toString();
          }
        case KPITypes.Expression:
          return this.resultNumber?.toFixed(decimalCount);
        case KPITypes.TextMultiline:
        case KPITypes.Html:
        case KPITypes.Text:
          return this.resultText;
        case KPITypes.Date:
          return this.resultDate ? toLocaleDateShort(this.resultDate) : undefined;
        default:
          return undefined;
      }
    }
  }

  // Whether the audit run left a verdict on this answer. AIAssessedOn is what the run stamps, so it
  // is present exactly when there is an AI row - the value columns may legitimately be empty, which
  // is how "inspected, but no pass or fail" is recorded.
  hasAiVerdict(): boolean {
    return this.aiAssessedOn !== undefined;
  }

  // The AI answer read out of the same column the human answers in, so the two can be compared. The
  // mirror of getValue: undefined means the AI gave no answer this field could hold.
  getAiValue(t?: TFunction<string[]>): string | undefined {
    const type = this.definition?.type ?? this.kpi?.type;
    const decimalCount = this.definition?.decimalCount ?? this.kpi?.decimalCount ?? 0;
    if (type !== undefined) {
      switch (type) {
        case KPITypes.Choice:
          const choiceKey = this.aiResultNumber === undefined ? undefined : Math.floor(this.aiResultNumber);
          const choice = this.definition?.choices.find((item) => item.choiceKey === choiceKey);

          return this.aiResultNumber !== undefined
            ? `${this.aiResultNumber} - ${choice?.value ?? this.aiResultText}`
            : undefined;
        case KPITypes.Number:
        case KPITypes.Expression:
          return this.aiResultNumber?.toFixed(decimalCount);
        case KPITypes.SuccesError:
          if (this.aiResultNumber === undefined) return undefined;
          if (t) {
            return this.aiResultNumber > 0 ? t('translation:General.Words.Yes') : t('translation:General.Words.No');
          }

          return this.aiResultNumber.toString();
        case KPITypes.TextMultiline:
        case KPITypes.Html:
        case KPITypes.Text:
          return this.aiResultText;
        case KPITypes.Date:
          return this.aiResultDate ? toLocaleDateShort(this.aiResultDate) : undefined;
        default:
          return undefined;
      }
    }
  }

  // Whether the AI verdict can be taken over into the answer. A status the AI could not express in
  // this field's shape (NotApplicable, NotInspectable, Unknown) leaves nothing to copy.
  hasAiAnswer(): boolean {
    return this.getAiValue() !== undefined;
  }

  getRunOutcome = (): boolean | undefined => {
    if (!this.kpi) return undefined;

    if (this.kpi.type !== KPITypes.SuccesError) {
      return undefined; //not applicable for numeric KPIs
    } else {
      if (this.resultNumber === 1) {
        return true; //accepted
      } else {
        return false; //rejected
      }
    }
  };

  // How long the COMMENT is: the length of what was actually written, not of the markup the editor
  // stores it in. An emptied comment still holds its paragraph tags, which counted as characters and
  // satisfied a required comment on their own; word wrap is off because the converter's own line
  // breaks are not written characters either. Same rule as the audit conclusion's minimum length.
  getCommentLength = (): number => {
    return htmlToText(this.resultComment, false).trim().length;
  };

  clone(): KPIData {
    const output = new KPIData();
    output.kpiId = this.kpiId;
    output.kpiDataId = this.kpiDataId;
    output.kpiDefinitionRevisionId = this.kpiDefinitionRevisionId;
    output.definition = this.definition?.clone();
    output.created = new Date(this.created);
    output.createdBy = this.createdBy;
    output.createdById = this.createdById;
    output.modified = this.modified ? new Date(this.modified) : undefined;
    output.modifiedBy = this.modifiedBy;
    output.modifiedById = this.modifiedById;
    output.resultDate = this.resultDate ? new Date(this.resultDate) : undefined;
    output.resultNumber = this.resultNumber;
    output.resultText = this.resultText;
    output.resultComment = this.resultComment;
    output.isSeeded = this.isSeeded;
    output.resultEvidence = this.resultEvidence;
    output.aiResultNumber = this.aiResultNumber;
    output.aiResultText = this.aiResultText;
    output.aiResultDate = this.aiResultDate ? new Date(this.aiResultDate) : undefined;
    output.aiResultComment = this.aiResultComment;
    output.aiAssessedOn = this.aiAssessedOn ? new Date(this.aiAssessedOn) : undefined;
    output.aiRunRef = this.aiRunRef;
    output.contexts = this.contexts.map((c) => c.clone());
    output.kpi = this.kpi; //not not clone because this would cause a loopt. this must be a ref to the kpi that this data belongs to

    return output;
  }

  //The values of this row as a new, unsaved answer for another task (save as new task, save as
  //template): no row id, no contexts and no stamp or audit fields, since those name the row and the
  //task it was recorded on. The server appends every context a row arrives with, so a copy that kept
  //them would be attached to the source task as well.
  cloneAsNewAnswer(): KPIData {
    const output = new KPIData();
    output.kpiId = this.kpiId;
    output.resultDate = this.resultDate ? new Date(this.resultDate) : undefined;
    output.resultNumber = this.resultNumber;
    output.resultText = this.resultText;
    output.resultComment = this.resultComment;
    output.isSeeded = this.isSeeded;
    output.kpi = this.kpi;

    return output;
  }

  isEqual(item: KPIData): boolean {
    if (item.kpiId !== this.kpiId) return false;
    if (item.kpiDataId !== this.kpiDataId) return false;
    if (item.kpiDefinitionRevisionId !== this.kpiDefinitionRevisionId) return false;
    if (
      !!item.definition !== !!this.definition ||
      (item.definition !== undefined && this.definition !== undefined && !item.definition.isEqual(this.definition))
    ) {
      return false;
    }
    if (getDateTimeDiff(item.resultDate, this.resultDate)) return false;
    if (item.resultNumber !== this.resultNumber) return false;
    if (item.resultText !== this.resultText) return false;
    if (getDateTimeDiff(item.created, this.created)) return false;
    if (item.createdBy !== this.createdBy) return false;
    if (item.createdById !== this.createdById) return false;
    if (getDateTimeDiff(item.modified, this.modified)) return false;
    if (item.modifiedBy !== this.modifiedBy) return false;
    if (item.modifiedById !== this.modifiedById) return false;
    if (item.resultComment !== this.resultComment) return false;
    if (item.isSeeded !== this.isSeeded) return false;
    if (item.resultEvidence !== this.resultEvidence) return false;
    if (item.aiResultNumber !== this.aiResultNumber) return false;
    if (item.aiResultText !== this.aiResultText) return false;
    if (getDateTimeDiff(item.aiResultDate, this.aiResultDate)) return false;
    if (item.aiResultComment !== this.aiResultComment) return false;
    if (getDateTimeDiff(item.aiAssessedOn, this.aiAssessedOn)) return false;
    if (item.aiRunRef !== this.aiRunRef) return false;

    if (
      areDifferent(item.contexts, this.contexts, (a: KPIDataContext, b: KPIDataContext) => {
        return a.isEqual(b);
      }) === true
    ) {
      return false;
    }

    return true;
  }
}
